<template>
  <div class="card">
    <div class="banner-container">
      <img class="banner" :src="bannerImg" alt="ogp" />
    </div>
    <div class="text-container">
      <div>
        <div class="name-container">
          <img class="icon" :src="iconImg" alt="icon" width="40" height="40" />
          <div class="name">{{ name }}</div>
        </div>
        <div class="comment-container">
          <div class="comment">{{ comment }}</div>
        </div>
      </div>
      <div>
        <div class="match-container">
          <div class="match-label">
            <div v-if="numOfMatchedNovels > 0" v-html="$t('companyCardVerB.matchLabelWithCount', { matched: numOfMatchedNovels })"></div>
            <div v-else>{{ $t("companyCardVerB.matchLabelEmpty") }}</div>
          </div>
        </div>
        <div class="button-container">
          <button class="button-common-style detail-button" @click="onClickOpenDetail">{{ $t("companyCardVerB.detailButton") }}</button>
          <button class="button-common-style post-button" @click="onClickPost">{{ $t("companyCardVerB.postButton") }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { createUrlWithUtmParams, optimizeImage } from "@/lib/utils/url";
import { NolaAnalytics, NolaItemId, NolaPageName } from "@/lib/utils/analytics";
import { NolaNovelUrlGenerator } from "@/lib/utils/generator/nolanovelUrl";
import { showNotifyDialog } from "@/lib/dialog";

export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    company: Object as PropType<Company>,
    matchedNovelIds: [],
  },
  computed: {
    id() {
      const { company } = this;
      return company.id;
    },
    bannerImg() {
      const { company } = this;
      return optimizeImage(`${company.bannerImg.url}`);
    },
    iconImg() {
      const { company } = this;
      return optimizeImage(`${company.iconImg.url}`);
    },
    name() {
      const { company } = this;
      return company.name;
    },
    comment() {
      const { company } = this;
      return company.comment;
    },
    numOfMatchedNovels() {
      const { matchedNovelIds } = this;
      // 描画中の場合など
      if (!matchedNovelIds) return 0;
      return matchedNovelIds.length;
    },
  },
  methods: {
    onClickOpenDetail() {
      const { id } = this;

      const url = createUrlWithUtmParams(
        `${process.env.VUE_APP_NOLANOVEL_WEB}/company/${id}`,
        NolaPageName.EventCompany,
        NolaItemId.CompanyCard
      );
      this.$emit("openDetailFrame", url);
    },
    async onClickPost() {
      try {
        const url = process.env.VUE_APP_NOLANOVEL_WEB;

        if (!url) {
          throw new Error("NolaノベルのWebサイトURLが環境変数に含まれていません。");
        }

        const service = new NolaNovelUrlGenerator(process.env.VUE_APP_NOLANOVEL_WEB!);

        const { tag } = this.company;

        const postNovelForCompanyUrl = await service.genPostNovelForCompanyUrl(tag);

        // iframeを開いたままwindow.openで開くと、iframeの中で遷移するため、aタグでプログラム的にクリックする
        const link = document.createElement("a");
        link.href = createUrlWithUtmParams(
          postNovelForCompanyUrl,
          NolaPageName.NolaNovel,
          NolaItemId.PostToNolaNovelFromCompany
        );
        link.target = "_blank";
        link.click();
        link.remove();

        const nolaAnalytics = new NolaAnalytics(this.$gtm, this.$store);
        nolaAnalytics.logButtonEvent(NolaItemId.PostToNolaNovelFromCompanyB, this.id);
      } catch (_) {
        await showNotifyDialog({
          title: this.$t("companyCardVerB.errorDialog.title") as string,
          content: this.$t("companyCardVerB.errorDialog.content") as string,
        });
      }
    },
  },
});

interface Props {
  company: Company;
  matchedNovelIds: Array<string>;
}

interface Data {}

interface Computed {
  id: string;
  bannerImg: string;
  iconImg: string;
  name: string;
  comment: string;
  numOfMatchedNovels: number;
}

interface Methods {
  onClickOpenDetail(): void;
  onClickPost(): void;
}

type Company = {
  [key: string]: any;
};
</script>

<style lang="scss" scoped>
.card {
  box-sizing: border-box;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  width: 300px;
  transition: all 0.3s ease 0s;
  // 画像がはみ出さないように
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  color: var(--color-text-normal);

  .banner-container {
    // 横幅はcardの幅と同じになるように
    width: 100%;
    // 縦幅は固定値
    height: 90px;
    overflow: hidden;

    .banner {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .text-container {
    box-sizing: border-box;
    padding-top: 16px;
    padding-bottom: 16px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-grow: 1;

    .name-container {
      display: flex;
      align-items: center;
      margin: 0 12px 0;

      .icon {
        border-radius: var(--radius-circle);
        border: 1px solid var(--color-border);
      }

      .name {
        font-weight: var(--font-weight-bold);
        line-height: 20px;
        font-size: var(--font-size-md);
        margin-left: 8px;
        color: var(--color-text-normal);
      }
    }

    .comment-container {
      position: relative;
      box-sizing: border-box;
      background-color: var(--color-surface-muted);
      padding: 8px;
      margin: 16px 12px 0;
      border-radius: var(--radius-md);

      .comment {
        font-weight: 350;
        line-height: 18px;
        font-size: var(--font-size-xs);
        display: -webkit-box;
        line-clamp: 3;
        -webkit-line-clamp: 3; /* 3行に制限 */
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        color: var(--color-text-normal);
      }
    }

    .match-container {
      margin-top: 16px;
      text-align: center;

      .match-label {
        line-height: 20px;
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-bold);
        color: var(--color-text-normal);
      }
    }

    .button-container {
      display: flex;
      justify-content: space-between;
      margin: 16px 14px 0;
      column-gap: 8px;

      .button-common-style {
        padding: 5px 0;
        border: 0;
        border-radius: var(--radius-xs);
        width: 100%;
        color: var(--color-text-on-fixed-bg);
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-bold);
        line-height: 28px;
        cursor: pointer;
      }

      .detail-button {
        background: var(--color-primary);
      }

      .post-button {
        background: #e9793d;
      }
    }
  }
}
</style>
